import {
  Badge,
  Box,
  Grid,
  GridItem,
  HStack,
  Skeleton,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';

import type { CompanyLeverageSignal } from '../hooks/useRolePostingSignals';

const LEVERAGE_COLORS: Record<string, string> = {
  high: 'green',
  medium: 'orange',
  low: 'gray',
};

const LEVERAGE_LABELS: Record<string, string> = {
  high: 'HIGH',
  medium: 'MED',
  low: 'LOW',
};

const formatDays = (days: number): string => {
  if (days >= 1) return `${Math.round(days)}d`;
  return '<1d';
};

const SignalStatBox = ({
  label,
  value,
  subtext,
  color = 'gray.800',
}: {
  label: string;
  value: string;
  subtext?: string;
  color?: string;
}) => (
  <Box bg="gray.50" px={3} py={2} borderRadius="md" textAlign="center">
    <Text fontSize="2xs" color="gray.500" fontWeight="semibold" mb={0.5}>
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="bold" color={color}>
      {value}
    </Text>
    {subtext && (
      <Text fontSize="2xs" color="gray.400" mt={0.5}>
        {subtext}
      </Text>
    )}
  </Box>
);

const CompanySignalRow = ({
  signal,
  salaryFormatter,
  isCurrentEmployer,
  roleName,
}: {
  signal: CompanyLeverageSignal;
  salaryFormatter: (value: number) => string;
  isCurrentEmployer: boolean;
  roleName: string | null;
}) => {
  const roleContext = roleName ? ` for ${roleName}` : '';

  let postingSalaryDisplay = '\u2014';
  let postingSalaryColor = 'gray.800';
  let deltaText: string | undefined;
  if (signal.postingSalary !== null) {
    postingSalaryDisplay = salaryFormatter(signal.postingSalary);
    if (signal.salaryDeltaPct !== null) {
      const sign = signal.salaryDeltaPct >= 0 ? '+' : '';
      deltaText = `${sign}${signal.salaryDeltaPct.toFixed(1)}% vs market`;
      if (signal.salaryDeltaPct > 5) {
        postingSalaryColor = 'green.500';
      } else if (signal.salaryDeltaPct < -5) {
        postingSalaryColor = 'red.500';
      }
    }
  }

  let timeToFillDisplay = '\u2014';
  let timeToFillColor = 'gray.800';
  let timeToFillSubtext: string | undefined;
  if (signal.timeToFill !== null) {
    timeToFillDisplay = formatDays(signal.timeToFill);
    if (signal.timeToFill > 45) {
      timeToFillColor = 'green.500';
      timeToFillSubtext = 'Hard to fill';
    } else if (signal.timeToFill > 30) {
      timeToFillColor = 'orange.500';
      timeToFillSubtext = 'Moderate';
    } else {
      timeToFillColor = 'gray.600';
      timeToFillSubtext = 'Quick fill';
    }
  }

  let activeDisplay = '\u2014';
  let activeColor = 'gray.800';
  let activeSubtext: string | undefined;
  if (signal.activePostings !== null) {
    activeDisplay = String(signal.activePostings);
    if (signal.activePostings > 10) {
      activeColor = 'green.500';
      activeSubtext = 'Strong demand';
    } else if (signal.activePostings > 5) {
      activeColor = 'orange.500';
      activeSubtext = 'Moderate';
    } else if (signal.activePostings > 0) {
      activeColor = 'gray.600';
      activeSubtext = 'Limited';
    } else {
      activeSubtext = 'No openings';
    }
  }

  return (
    <Box>
      <HStack spacing={2} mb={2}>
        <Text fontSize="xs" fontWeight="semibold" color="gray.600">
          {signal.companyName}
        </Text>
        {isCurrentEmployer && (
          <Badge size="sm" colorScheme="blue" fontSize="2xs">
            Current
          </Badge>
        )}
        {signal.leverageLevel && (
          <Tooltip
            label={`Overall negotiation leverage${roleContext} based on time-to-fill and active postings`}
            fontSize="xs"
            hasArrow
          >
            <Badge
              size="sm"
              colorScheme={LEVERAGE_COLORS[signal.leverageLevel]}
              fontSize="2xs"
            >
              {LEVERAGE_LABELS[signal.leverageLevel]} Leverage
            </Badge>
          </Tooltip>
        )}
      </HStack>
      <Grid templateColumns="repeat(3, 1fr)" gap={2}>
        <GridItem>
          <Tooltip
            label={`Average salary in current job postings${roleContext}`}
            fontSize="xs"
            hasArrow
          >
            <Box>
              <SignalStatBox
                label="Posting Salary"
                value={postingSalaryDisplay}
                subtext={deltaText}
                color={postingSalaryColor}
              />
            </Box>
          </Tooltip>
        </GridItem>
        <GridItem>
          <Tooltip
            label={`Average days to fill${roleContext} \u2014 longer = more leverage`}
            fontSize="xs"
            hasArrow
          >
            <Box>
              <SignalStatBox
                label="Time to Fill"
                value={timeToFillDisplay}
                subtext={timeToFillSubtext}
                color={timeToFillColor}
              />
            </Box>
          </Tooltip>
        </GridItem>
        <GridItem>
          <Tooltip
            label={`Number of active postings${roleContext}`}
            fontSize="xs"
            hasArrow
          >
            <Box>
              <SignalStatBox
                label="Active Postings"
                value={activeDisplay}
                subtext={activeSubtext}
                color={activeColor}
              />
            </Box>
          </Tooltip>
        </GridItem>
      </Grid>
    </Box>
  );
};

interface DemandLeverageSignalsProps {
  signals: CompanyLeverageSignal[];
  currentCompanyId: number | null;
  salaryFormatter: (value: number) => string;
  isLoading: boolean;
  error: string | null;
  roleName: string | null;
}

export const DemandLeverageSignals = ({
  signals,
  currentCompanyId,
  salaryFormatter,
  isLoading,
  error,
  roleName,
}: DemandLeverageSignalsProps) => {
  if (isLoading) {
    return <Skeleton height="100px" borderRadius="md" />;
  }

  if (error) {
    return (
      <Box bg="orange.50" p={3} borderRadius="md" textAlign="center">
        <Text color="orange.600" fontSize="xs">
          {error}
        </Text>
      </Box>
    );
  }

  // Only show companies that have active postings for this role
  const withActivePostings = signals.filter(
    (s) => s.activePostings !== null && s.activePostings > 0
  );

  if (withActivePostings.length === 0) return null;

  // Current employer first, then sort by posting salary descending
  const sorted = [...withActivePostings].sort((a, b) => {
    if (a.companyId === currentCompanyId) return -1;
    if (b.companyId === currentCompanyId) return 1;
    const salaryA = a.postingSalary ?? 0;
    const salaryB = b.postingSalary ?? 0;
    return salaryB - salaryA;
  });

  const roleContext = roleName ? ` for ${roleName}` : '';

  return (
    <Box bg="gray.50" borderRadius="md" p={4}>
      <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={3}>
        Demand & Leverage Signals{roleContext}
      </Text>
      <VStack align="stretch" spacing={3}>
        {sorted.map((signal) => (
          <CompanySignalRow
            key={signal.companyId}
            signal={signal}
            salaryFormatter={salaryFormatter}
            isCurrentEmployer={signal.companyId === currentCompanyId}
            roleName={roleName}
          />
        ))}
      </VStack>
    </Box>
  );
};
